import { useEffect, useRef, useState } from "react";
import { ChevronUp, ChevronDown } from "lucide-react";

/**
 * Elegant circular scroll button.
 * - Appears after scrolling ~300px.
 * - RTL/LTR aware via CSS logical properties.
 * - Icon direction follows the last scroll direction:
 *     scrolling down  -> ChevronUp   (click = go to top)
 *     scrolling up    -> ChevronDown (click = go to bottom)
 * - Circular border acts as a page-scroll progress ring in primary color.
 * - Semi-transparent primary background with backdrop blur.
 * - Lifted above mobile bottom bars using safe-area inset + extra offset.
 */
export function ScrollToTop() {
  const [visible, setVisible] = useState(false);
  const [progress, setProgress] = useState(0); // 0..1
  const [direction, setDirection] = useState<"up" | "down">("down");
  const lastY = useRef(0);

  useEffect(() => {
    let raf = 0;
    lastY.current = window.scrollY;

    const update = () => {
      raf = 0;
      const y = window.scrollY;
      const doc = document.documentElement;
      const max = Math.max(1, (doc.scrollHeight || 0) - window.innerHeight);
      const p = Math.min(1, Math.max(0, y / max));
      setProgress(p);
      setVisible(y > 300);

      const dy = y - lastY.current;
      if (Math.abs(dy) > 2) {
        setDirection(dy > 0 ? "down" : "up");
        lastY.current = y;
      }
    };

    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  const handleClick = () => {
    const prefersReduced =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const target =
      direction === "down"
        ? 0
        : document.documentElement.scrollHeight - window.innerHeight;
    window.scrollTo({ top: target, behavior: prefersReduced ? "auto" : "smooth" });
  };

  // Progress ring geometry
  const size = 48;
  const stroke = 2.5;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dashOffset = c * (1 - progress);

  const Icon = direction === "down" ? ChevronUp : ChevronDown;

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={
        direction === "down"
          ? "العودة إلى الأعلى / Scroll to top"
          : "الانتقال إلى الأسفل / Scroll to bottom"
      }
      title={direction === "down" ? "العودة إلى الأعلى" : "الانتقال إلى الأسفل"}
      aria-hidden={!visible}
      tabIndex={visible ? 0 : -1}
      style={{
        insetInlineEnd: "1rem",
        // Lifted above bottom bars on mobile; smaller offset on larger screens is fine too.
        bottom: "calc(5rem + env(safe-area-inset-bottom, 0px))",
        width: size,
        height: size,
      }}
      className={[
        "fixed z-[60] rounded-full",
        "flex items-center justify-center",
        // Semi-transparent primary background
        "bg-primary/20 text-primary backdrop-blur-md",
        "supports-[backdrop-filter]:bg-primary/15",
        "shadow-lg shadow-primary/20",
        "transition-all duration-300 ease-out will-change-transform",
        "hover:-translate-y-0.5 hover:bg-primary/25 hover:shadow-xl hover:shadow-primary/30",
        "active:translate-y-0 active:scale-95",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        "sm:bottom-6",
        visible
          ? "opacity-100 translate-y-0 pointer-events-auto"
          : "opacity-0 translate-y-3 pointer-events-none",
      ].join(" ")}
    >
      {/* Progress ring border */}
      <svg
        className="pointer-events-none absolute inset-0 -rotate-90"
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        aria-hidden="true"
      >
        {/* Track */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="currentColor"
          strokeOpacity="0.2"
          strokeWidth={stroke}
        />
        {/* Progress */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="currentColor"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={dashOffset}
          style={{ transition: "stroke-dashoffset 120ms linear" }}
        />
      </svg>

      <Icon
        className="relative h-5 w-5 transition-transform duration-300"
        strokeWidth={2.5}
        aria-hidden="true"
      />
    </button>
  );
}

export default ScrollToTop;
